import { IconAlertCircle } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError, errorReason } from '../lib/api';
import { RequestIdCopy } from './RequestIdCopy';
import { Button } from './ui/Button';

// Failed-query surface: a destructive `.alert` (spec §3.4/§3.15) with the
// derived message, a Retry button, and — when the failure is an `ApiError`
// carrying the client request id — a copyable id to quote to support.

export type ErrorStateProps = {
  readonly error?: unknown;
  /**
   * Omit it — or pass `undefined` — and no Retry renders. The `| undefined` is
   * load-bearing under `exactOptionalPropertyTypes`, which makes an explicit
   * `undefined` a DIFFERENT type from an absent key: without it a caller
   * deciding at runtime whether a failure is retryable could not say so, and
   * had to spread a conditional object (`{...(isForbidden(err) ? {} : {onRetry})}`)
   * to express "no retry" at all. `message` already carried it for the same
   * reason.
   *
   * For a 403 the answer is not "omit the retry" but `<DeniedState/>`, which
   * cannot express one — see its docblock.
   */
  readonly onRetry?: (() => void) | undefined;
  /** Override the derived message (e.g. a view-specific string). `undefined` keeps the derived one. */
  readonly message?: string | undefined;
};

function requestIdOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.requestId : undefined;
}

export function ErrorState(props: ErrorStateProps): JSX.Element {
  // The reason, not the wire debug (#801 pain point #2). Every failed route
  // renders through here, so "Something went wrong." was the ONLY thing a
  // maintainer ever saw — the API's RFC 9457 `detail` ("account is not approved
  // for the waitlist", "installation was suspended") went straight in the bin,
  // and each route looked identically, unactionably broken. #916 already built
  // the precedence for inline mutation errors; this is the same rule one level
  // up, where it covers every route at once instead of one form.
  //
  // 401 keeps its own copy, and that copy had been saying the WRONG THING. It
  // read "You don't have access to this." — the 403 sentence — on the one status
  // that means the opposite: the credential is gone, so signing in fixes it.
  // Nothing caught it because a real 401 barely reaches this branch at all
  // (`apiFetch` latches `markSessionExpired()` and `resolveAppGate` returns
  // `login` first), and because until `<DeniedState/>` existed there was no
  // other state for the sentence to belong to. Now there is, and the two must
  // not both claim it: this one is "sign in", that one is "you may not".
  const message = (): string => {
    if (props.message) return props.message;
    if (props.error instanceof ApiError && props.error.status === 401) {
      return t('errors.unauthorized');
    }
    return errorReason(props.error, t('errors.generic'));
  };

  return (
    <div class="alert alert--icon alert--destructive" role="alert">
      <IconAlertCircle />
      <p class="alert__title">{message()}</p>
      <div class="alert__desc error-state__body">
        <div class="error-state__actions">
          <Show when={props.onRetry}>
            <Button variant="outline" size="sm" onClick={() => props.onRetry?.()}>
              {t('errors.retry')}
            </Button>
          </Show>
          <Show when={requestIdOf(props.error)}>{(id) => <RequestIdCopy requestId={id()} />}</Show>
        </div>
        <Show when={requestIdOf(props.error)}>
          {(id) => <code class="error-state__id">{id()}</code>}
        </Show>
      </div>
    </div>
  );
}
